import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { StyledSubBreadcrumbs } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/styled';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Flex, Text } from '@mantine/core';
import { useCallback, useContext, useMemo, useState } from 'react';
import { TooltipButton } from '@dealstack/frontend/shared/components/Table/MantineTable/TooltipButton/TooltipButton';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { BasicMantineTopToolbar } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTopToolbar';
import { DealClassifierFiltersDto, DealClassifierTct } from '@dealstack/shared/lib/generated';
import { CreateClassifierDrawer } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/CreateClassifierDrawer/CreateClassifierDrawer';
import { EditClassifierDrawer } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/EditClassifierDrawer/EditClassifierDrawer';
import {
	createNewClassifier,
	isNewClassifier,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { generatePath } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { getClassifierTableColumns } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/ClassifierTableColumns';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	GetDealClassifierFilters,
	useGetDealClassifiers,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/hooks/useGetDealClassifiers';
import { Classifier } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/types';
import { Row, Table } from '@tanstack/react-table';
import {
	DealClassifierWithSubRows,
	mapClassifiersToTableData,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils/mapClassifiersToTableData';

export const ClassifierSettings = () => {
	const tCommonUi = useTranslationCommonUi();
	const tAdminDashboard = useTranslationAdminDashboard();
	const { deal, dealId } = useContext(DealContext);
	const [classifier, setClassifier] = useState<Classifier>(createNewClassifier());

	const [showCreateUpdateClassificationDrawer, setShowCreateUpdateClassificationDrawer] =
		useState(false);

	const onRowClick = useCallback(
		(classifier: DealClassifierTct) => {
			setClassifier({
				dealClassifierId: classifier.id,
				classifierId: classifier.classifierId,
				archive: classifier.deleted,
				name: classifier.name,
				dealId,
				classifierParentId: classifier.classifierParentId,
				children: classifier.children,
			});
			setShowCreateUpdateClassificationDrawer(true);
		},
		[setClassifier, setShowCreateUpdateClassificationDrawer, dealId]
	);

	const columns = useMemo(
		() =>
			getClassifierTableColumns({
				tAdminDashboard,
				tCommonUi,
				onRowClick,
			}),
		[tAdminDashboard, tCommonUi, onRowClick]
	);

	const initialTableOperations: UseMantineTableDataOperationsPartialProps = useMemo(
		() => ({
			initialPagination: {
				pageIndex: 0,
				pageSize: 10,
			},
			initialSorting: [
				{
					id: 'name',
					desc: false,
				},
			],
		}),
		[]
	);
	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting(columns, initialTableOperations);

	const { pagination, search, sortBy } = dataOperations ?? {};

	const filters: GetDealClassifierFilters = useMemo(() => {
		const filter: DealClassifierFiltersDto = {
			classifierName: search ?? undefined,
		};

		return {
			pagination,
			filter,
			// single sort for now
			sortBy: sortBy?.[0]
				? {
						key: sortBy[0].key,
						desc: sortBy[0].desc ?? false,
					}
				: undefined,
		};
	}, [pagination, search, sortBy]);

	const { classifiers: classifiersResponse, isClassifiersLoading } = useGetDealClassifiers({
		filters,
		dealId,
	});

	const handleOnCancel = () => {
		setClassifier(createNewClassifier());
		setShowCreateUpdateClassificationDrawer(false);
	};

	const classifiers = useMemo(
		() => classifiersResponse?.payload?.dealClassifiers,
		[classifiersResponse?.payload?.dealClassifiers]
	);
	const classifiersRowCount = useMemo(
		() => classifiersResponse?.payload?.meta.totalRowCount ?? 0,
		[classifiersResponse?.payload?.meta.totalRowCount]
	);

	const mantineTableData = useMemo(
		() => mapClassifiersToTableData(classifiers) ?? [],
		[classifiers]
	);

	useBreadcrumbs([
		{
			name: tCommonUi('breadcrumbs.deal_settings'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId: deal?.id ?? '' }),
		},
	]);

	return (
		<Flex direction="column" h="100%" w="100%" style={{ overflow: 'auto' }}>
			<ContainerWithHeader
				title={
					<StyledSubBreadcrumbs align="center" gap="0.5rem">
						<Text variant="p-sm">{deal?.name}</Text>
						<Icon variant="backslash" size="vlg" />
						<Text variant="p-sm">
							{tAdminDashboard('deal_settings.labels.deal_classifiers')}
						</Text>
					</StyledSubBreadcrumbs>
				}
			>
				<MantineTable<DealClassifierTct>
					enableExpanding={true}
					manualPagination={true}
					getRowId={(rowData: DealClassifierTct) => rowData.id.toString()}
					getSubRows={(row) => {
						return (row as DealClassifierWithSubRows).subRows;
					}}
					renderEmptyRowsFallback={() => (
						<>{tAdminDashboard('deal_settings.labels.no_data')}</>
					)}
					dataOperations={mantineTableDataOperations}
					columns={columns}
					rowCount={classifiersRowCount}
					enableRowSelection={false}
					renderBulkActionsMenu={false}
					isLoading={isClassifiersLoading}
					data={mantineTableData}
					enableFilters={false}
					enableGlobalFilter={false}
					enableFullScreenToggle={false}
					mantineTableBodyRowProps={({ row }: { row: Row<DealClassifierTct> }) => ({
						onClick: () => onRowClick(row.original),
						style: {
							cursor: 'pointer',
						},
					})}
					renderTopToolbar={({ table }: { table: Table<DealClassifierTct> }) => (
						<BasicMantineTopToolbar
							table={table}
							customActions={
								<TooltipButton
									leftSection={<Icon variant="plus" size="vlg" />}
									text={tAdminDashboard('deal_settings.team.buttons.add_new')}
									onClick={() => {
										setClassifier(() => createNewClassifier(dealId));
										setShowCreateUpdateClassificationDrawer(true);
									}}
								/>
							}
							hideFiltersButton={true}
						/>
					)}
				/>
			</ContainerWithHeader>

			{isNewClassifier(classifier) ? (
				<CreateClassifierDrawer
					classifier={classifier}
					showCreateUpdateClassificationDrawer={showCreateUpdateClassificationDrawer}
					handleOnCancel={handleOnCancel}
					setShowCreateUpdateClassificationDrawer={
						setShowCreateUpdateClassificationDrawer
					}
				/>
			) : (
				<EditClassifierDrawer
					classifier={classifier}
					showCreateUpdateClassificationDrawer={showCreateUpdateClassificationDrawer}
					handleOnCancel={handleOnCancel}
				/>
			)}
		</Flex>
	);
};
